import { GuideScreenshot } from '@/components/guide-screenshot';

export const meta = {
  title: 'WordCraft 使用评估指南：切换前如何检查 DOCX 兼容性',
  description:
    '用自己的文档评估 WordCraft，逐项检查字体、分页、表格、批注、修订与 DOCX 往返保存，再决定哪些工作适合迁移。',
  updated_at: '2026-11-05',
  guide: true,
  image: '/imgs/wordcraft/layout',
  imageAlt: 'WordCraft 图片环绕与页面布局示例',
  category: '文档与兼容性',
  readingMinutes: 10,
  toc: [
    { id: 'what-to-evaluate', title: '明确切换成功的标准' },
    { id: 'sample-files', title: '选择代表性文件' },
    { id: 'round-trip', title: '执行 DOCX 往返测试' },
    { id: 'layout-checks', title: '检查字体与版面' },
    { id: 'review-checks', title: '验证审阅与引用' },
    { id: 'delivery', title: '选择交付格式' },
    { id: 'decision', title: '作出实际使用决定' },
    { id: 'questions', title: '兼容性常见问题' },
  ],
};

考虑使用 WordCraft 时，最有意义的问题是：它能否完成你实际需要创建、修改和交换的文档？能够打开一个文件，只验证了流程的一部分。字体替换、分页变化或批注表现差异，可能要到保存并交给别人之后才会显现。

本指南提供一套可以用自己的文件执行的评估方法，而不是承诺所有功能与其他编辑器完全一致。第一次接触界面，可以先读 [App 入门指南](/wordcraft-app-guide)；需要编辑器和下载入口，可以回到[首页](/)。

<h2 id="what-to-evaluate">先定义什么叫“适合切换”</h2>

WordCraft 是 ArtCraft 开发的开源文字处理软件，支持 DOCX，并提供桌面与浏览器版本。[官方仓库](https://github.com/storytold/wordcraft)介绍项目，[开发路线图](https://github.com/storytold/wordcraft/blob/main/ROADMAP.md)可以帮助了解当前进展。产品声明应参考官方资料，是否适合自己的工作则需要实际文件来验证。

先列出三个必须保留的能力。学生可能关心标题结构、引用与可读的 PDF；小团队可能需要信头、报价表和客户可以继续编辑的文件；编辑人员则可能更依赖批注和修订信息。要求越具体，测试越容易得到有用结果。

再明确哪些差异可以接受。内部会议记录换了一行，可能不影响工作；需要印到固定表格上的文档，偏移一点就可能不能使用。同一种版面变化，在不同任务里的重要程度并不相同。

不要从“是否兼容所有文档”这种无法验证的问题开始。选几个明确任务，为每项任务写下通过条件。这样，一张看起来漂亮的首页截图就不会被误当成整套工作流已经通过测试。

<h2 id="sample-files">准备简单、日常和复杂三份文件</h2>

选择三份你熟悉的文档，并复制到单独的测试文件夹。简单文件可以是带标题和编号列表的两页信件；日常文件可以包含表格、标志图片和页码；复杂文件则选择真实工作里会遇到的多节报告、目录、批注或靠近页边界的图片。

复杂度应来自实际需求，不必随意下载一个极端测试文件。你要了解的是自己的文档是否适用。原件放在独立位置，不要拿临近截止日期且只有一份的材料直接试验。

记录每份原件的页数、纸型、主要字体和几个明显位置，例如第二页首行、表格合计行和插图下面的说明。如果仍能使用原来的编辑器，先导出一份参考 PDF，作为视觉基准，同时保留可编辑原件。

建立一个简短记录表，包含文件名、检查项、预期结果、实际结果和决定。记录“最后一行表格移动到第四页”，不要只写“格式不好”。具体描述有助于定位原因，也方便新版本发布后重复测试。

<GuideScreenshot
  base="/imgs/wordcraft/layout"
  alt="WordCraft 布局界面，文档包含图片环绕、首字下沉和页面设置"
  caption="图 1：复杂版式需要比纯文字更多的检查。截图展示了适合纳入代表性样本的几类元素。图片来源：ArtCraft / WordCraft。"
/>

<h2 id="round-trip">执行一次有控制的 DOCX 往返测试</h2>

往返测试是指：打开原始副本、进行小修改、另存新文件，再用收件人实际使用的软件检查结果。它验证的是一次真实交换，而不仅是导入步骤。对方用什么软件处理文件，就应把那个环境纳入检查。

1. 在 WordCraft 中打开原件的副本，修改前先查看版面。
2. 做一个容易识别的小改动，例如在测试段落末尾添加“周四已检查”。
3. 另存为 `report-wordcraft-test.docx`，不要覆盖原件。
4. 关闭并在 WordCraft 中重新打开刚保存的文件。
5. 用收件人的编辑器打开同一文件，与原件及参考 PDF 比较。
6. 把内容变化、版面差异和功能行为分别记录。

小改动可以减少干扰。如果先重写半份报告、重新设计所有页面，再比较页数，就很难判断变化究竟来自自己修改的内容，还是应用对文件的处理方式。

不要因为首页正常就立刻替换原件。检查最后一页、页眉页脚、注释和正文之外的信息。有些文件第一页十分简单，真正复杂的结构在后面的章节里。

网页版还要单独确认保存位置。找到实际下载或通过文件选择器保存的副本，再从磁盘打开它。误开没有修改的旧原件，会让保存问题看起来像兼容性问题。

<h2 id="layout-checks">按照字体、页面、表格的顺序检查</h2>

### 先解决字体基础

字体替换会改变字符宽度，进而影响整篇文档的换行。调整页边距或强制分页之前，先确认目标环境能否使用文档预期的字体。浏览器应用与本地应用并不一定拥有相同的字体访问能力。

准备一段包含常用文字、数字、标点及实际需要的其他语言字符的短段落，比较换行和间距。如果发生替换，可以提供有使用许可的字体，或把文档统一到参与者都能使用的字体。不要认为操作系统装过某个字体，浏览器编辑器就一定能直接使用它。

### 再比较相同的页面设置

核对纸张尺寸、方向、页边距、页眉和页脚距离。含有纵向与横向页面的文件，需要检查切换前后的两节。比较时使用接近的缩放比例，避免把屏幕显示差异误判成实际排版差异。

不要立即用手动分页修复每一行错位。这可能让当前副本看起来正常，却让下一次编辑更脆弱。先判断原因是字体、段落间距、对象位置还是页面设置，再采取针对性的调整。

### 最后查看表格与图片边界

优先检查最宽的表格，以及最靠近页末的表格。观察是否出现文字截断、列宽改变、行被拆开或表头表现不同。图片不能只看“是否还在”，还要检查它与说明文字和周围段落之间的关系。

可以在浮动对象前临时增加一句话，再撤销修改，观察版面如何响应。如果行为无法满足这份文档的维护需求，可以在测试副本里简化布局，或继续让这类文件保留原有工作方式。

<h2 id="review-checks">把审阅与引用单独验证</h2>

审阅流程值得单独测试。选择含有一条已知批注和一处已知修订的副本，检查能否辨认批注对应的文字、阅读批注内容，以及区分插入和删除。保存后，请收件人在平时使用的应用里查看同一处信息。

<GuideScreenshot
  base="/imgs/wordcraft/review"
  alt="WordCraft 审阅功能区与文档中的修订和批注"
  caption="图 2：屏幕上能显示标记，只是审阅流程的一部分；保存后的 DOCX 和接收方软件也需要检查。图片来源：ArtCraft / WordCraft。"
/>

写清楚接受或拒绝某处修订后应该发生什么，再逐个测试。不要在尚未理解修订内容时就把“全部接受”作为第一次实验。文档看起来干净，可能只是部分信息被主动删除，不能单凭外观判断操作正确。

引用检查也应从小样本开始。选择可以手动核对的几个标题、脚注和交叉引用，确认标题顺序、引用目标以及注释标记与文字的对应关系。移动章节或修改结构后，更新生成内容，并再次检查结果。

<GuideScreenshot
  base="/imgs/wordcraft/references"
  alt="WordCraft 引用选项卡，文档中展示目录与引用相关工具"
  caption="图 3：目录和引用需要与实际文档结构一致，移动章节或改变分页后尤其要重新核对。图片来源：ArtCraft / WordCraft。"
/>

如果工作依赖宏、专用插件、自动化、受保护表单或嵌入对象，把每一项依赖列出来。支持打开 DOCX，并不能证明文件关联的所有行为都得到支持。查阅上游针对具体功能的说明，并在迁移前验证所需操作。

<h2 id="delivery">根据收件人的任务选择交付格式</h2>

对方需要继续修改内容时，应提供已经验证过的可编辑文件；只需要阅读、审批或打印时，可以提供 PDF。通常最实用的做法是两份都保留：作者维护可编辑主文件，另行生成用于阅读的交付副本。

使用成对且容易识别的名称，例如 `workshop-plan-review.docx` 与 `workshop-plan-review.pdf`。如果两者不是同一轮修改的结果，需要明确标注，避免有人批准旧 PDF，而另一个人继续修改更新的 DOCX。

用独立阅读器打开实际导出的 PDF。检查第一页和最后一页、长表格、链接及需要复制的文字。导出命令成功只是验证开始，不代表交付结果已检查完毕。需要在线制作与保存的完整步骤，可以继续看[在线写作指南](/wordcraft-online-guide)。

<h2 id="decision">按文档类型作出决定</h2>

把结果分为三种：可以日常使用、经过明确调整后可用、继续保留原有流程。调整可能是统一字体或简化图片位置；阻碍可能是某个必需的审阅行为无法在交换后保留。

先从低风险内部笔记开始，再逐步处理复杂客户模板。让同事检查一次真实交付，然后再考虑团队范围的切换。记录版本和测试日期，新版本到来时便能用同一组样本复查。

这套记录也适合反馈问题。提供最小复现文件、准确步骤和预期结果，比只发一张错位截图更容易帮助维护者理解情况。分享复现材料前移除私人内容，同时保留能触发问题的结构。

<h2 id="questions">兼容性常见问题</h2>

### 能打开 DOCX 就代表完全兼容吗？

不能。打开、保存、重新打开和交给其他应用处理，是不同阶段。重要文件应按上面的往返流程分别验证。

### 每一处分页都必须相同吗？

取决于任务。普通笔记可能允许换行变化，固定表单则可能不行。先定义要求，再比较字体和页面设置，不要一开始就修改正文来迁就差异。

### 网页版与桌面版的测试结果可以通用吗？

不要直接这样推断。版本、可用字体和文件操作方式可能不同，应测试真正准备使用的环境。[网页版指南](/wordcraft-web-version-guide)说明了浏览器特有的检查项。

### 有没有更简短的选型概览？

可以先读 [WordCraft 与 Microsoft Word 对比](/wordcraft-vs-microsoft-word)，再用本文验证自己的必要条件。需要下载与编辑器时，回到[首页](/)即可找到入口。
